import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { map, Observable } from 'rxjs';
import type { ListInterface } from '../models/list-interface';
import type { PatientInterface } from '../models/patient.interface';
import { PatientUpdateDtoInteface } from '../models/patient-update-dto.interface';

@Injectable({
  providedIn: 'root'
})
export class PatientService {
  constructor(private readonly apollo: Apollo) { }

  getPatientById(patientId: number): Observable<PatientInterface> {
    return this.apollo.query<{ patientById: PatientInterface }>({
      query: gql`query patientById ($patientId: Long!) {
        patientById (patientId: $patientId) {
          id
          petName
          ownerName
        }
      }`,
      variables: { patientId }
    }).pipe(map(res => res.data.patientById));
  }

  getPatientByExternalId(externalId: string): Observable<PatientInterface> {
    return this.apollo.query<{ patientByExternalId: PatientInterface }>({
      query: gql`query patientById ($externalId: String!) {
        patientByExternalId (patientExternalId: $externalId) {
          id
          petName
          ownerName
        }
      }`,
      variables: { externalId }
    }).pipe(map(res => res.data.patientByExternalId));
  }

  searchPatients(searchText: string, clinicId: number): Observable<PatientInterface[]> {
    return this.apollo.query<SearchPatientsReponseInterface>({
      query : gql`query searchPatients($searchText: String!, $clinicId: Long!) {
        patients(
          clinicId: $clinicId,
          order: { petName: ASC },
					where : { and: [
            {	or: [
              { petName: { startsWith: $searchText } },
              { ownerName: { startsWith: $searchText } },
						] }
					]}
        ) {
          items {
            id
            petName
            ownerName
          }
        }
      }`,
      variables: { searchText, clinicId }
    }).pipe(map(res => res.data.patients.items));
  }

  getPatientByStudyId(studyId: number): Observable<PatientInterface> {
    return this.apollo.query<{ patientByStudyId: PatientInterface }>({
      query: gql`query patientByStudyId($studyId: Long!) {
        patientByStudyId(studyId: $studyId) {
          id
          petName
          ownerName
          sexe
          speciesName
          birthDate
          pid
          speciesId
          sterilisationStatus
        }
      }`,
      variables: { studyId }
    }).pipe(map(res => res.data.patientByStudyId));
  }

  updatePatient(id: number, dto: PatientUpdateDtoInteface): Observable<PatientInterface | undefined> {
    return this.apollo.mutate<{ updatePatient: PatientInterface }>({
      mutation: gql`mutation updatePatient($id: Long!, $dto: PatientUpdateDtoInput!) {
        updatePatient(id: $id, dto: $dto) {
          id
          petName
          ownerName
        }
      }`,
      variables: { id, dto }
    }).pipe(map(res => res.data?.updatePatient));
  }
}

export interface SearchPatientsReponseInterface {
  patients: ListInterface<PatientInterface>;
}
